import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router-dom';
import moment from 'moment';
import qs from 'query-string';
import isEqual from 'lodash.isequal';
import { useTranslation } from 'react-i18next';
//
import filtersMeta from './filtersMeta.js';
import { useAppConfig } from '@state';
import { useDebounce, useSearchParams } from '@hooks';
import { utils, hotkeys } from '@ohif/core';

import { Button } from 'primereact/button';

import {
  Icon,
  StudyListExpandedRow,
  EmptyStudies,
  StudyListTable,
  StudyListPagination,
  StudyListFilter,
  TooltipClipboard,
  Header,
  useModal,
  AboutModal,
  UserPreferences,
  LoadingIndicatorProgress,
} from '@ohif/ui';

import i18n from '@ohif/i18n';
import { LaudoService } from '../../service/LaudoService.js';
import { ServiceApi } from '../../service/Api.js';
import { Dialog } from 'primereact/dialog';
import { ProgressBar } from 'primereact/progressbar';
import { HistoricoService } from '../../service/HistoricoService.js';

const { sortBySeriesDate } = utils;

const { availableLanguages, defaultLanguage, currentLanguage } = i18n;

const seriesInStudiesMap = new Map();

/**
 * TODO:
 * - debounce `setFilterValues` (150ms?)
 */
function WorkList({
  data: studies,
  dataTotal: studiesTotal,
  isLoadingData,
  dataSource,
  hotkeysManager,
  dataPath,
  servicesManager,
}) {
  const { uiNotificationService } = servicesManager.services;
  const { hotkeyDefinitions, hotkeyDefaults } = hotkeysManager;
  const { show, hide } = useModal();
  const { t } = useTranslation();
  // ~ Modes
  const [appConfig] = useAppConfig();
  // ~ Filters
  const searchParams = useSearchParams();
  const navigate = useNavigate();
  const STUDIES_LIMIT = 101;
  const queryFilterValues = _getQueryFilterValues(searchParams);
  const [filterValues, _setFilterValues] = useState({
    ...defaultFilterValues,
    ...queryFilterValues,
  });

  const debouncedFilterValues = useDebounce(filterValues, 200);
  const { resultsPerPage, pageNumber, sortBy, sortDirection } = filterValues;

  /*
   * The default sort value keep the filters synchronized with runtime conditional sorting
   * Only applied if no other sorting is specified and there are less than 101 studies
   */

  const canSort = studiesTotal < STUDIES_LIMIT;
  const shouldUseDefaultSort = sortBy === '' || !sortBy;
  const sortModifier = sortDirection === 'descending' ? 1 : -1;
  const defaultSortValues =
    shouldUseDefaultSort && canSort
      ? { sortBy: 'patientName', sortDirection: 'ascending' }
      : {};

  //const sortedStudies = studies;
  const sortedStudies = studies['Studies'] ? studies['Studies'] : [];

  // if (canSort) {
  //   studies.sort((s1, s2) => {
  //     if (shouldUseDefaultSort) {
  //       const ascendingSortModifier = -1;
  //       return _sortStringDates(s1, s2, ascendingSortModifier);
  //     }

  //     const s1Prop = s1[sortBy];
  //     const s2Prop = s2[sortBy];

  //     if (typeof s1Prop === 'string' && typeof s2Prop === 'string') {
  //       return s1Prop.localeCompare(s2Prop) * sortModifier;
  //     } else if (typeof s1Prop === 'number' && typeof s2Prop === 'number') {
  //       return (s1Prop > s2Prop ? 1 : -1) * sortModifier;
  //     } else if (!s1Prop && s2Prop) {
  //       return -1 * sortModifier;
  //     } else if (!s2Prop && s1Prop) {
  //       return 1 * sortModifier;
  //     } else if (sortBy === 'studyDate') {
  //       return _sortStringDates(s1, s2, sortModifier);
  //     }

  //     return 0;
  //   });
  // }

  // ~ Rows & Studies
  const [expandedRows, setExpandedRows] = useState([]);
  const [studiesWithSeriesData, setStudiesWithSeriesData] = useState([]);

  const numOfStudies = studiesTotal;

  const setFilterValues = val => {
    if (filterValues.pageNumber === val.pageNumber) {
      val.pageNumber = 1;
    }
    _setFilterValues(val);
    setExpandedRows([]);
  };

  const onPageNumberChange = newPageNumber => {
    const oldPageNumber = filterValues.pageNumber;
    const rollingPageNumberMod = Math.floor(101 / filterValues.resultsPerPage);
    const rollingPageNumber = oldPageNumber % rollingPageNumberMod;
    const isNextPage = newPageNumber > oldPageNumber;
    const hasNextPage =
      Math.max(rollingPageNumber, 1) * resultsPerPage < numOfStudies;

    if (isNextPage && !hasNextPage) {
      return;
    }

    setFilterValues({ ...filterValues, pageNumber: newPageNumber });
  };

  const onResultsPerPageChange = newResultsPerPage => {
    setFilterValues({
      ...filterValues,
      pageNumber: 1,
      resultsPerPage: Number(newResultsPerPage),
    });
  };

  // Set body style
  useEffect(() => {
    document.body.classList.add('bg-black');
    return () => {
      document.body.classList.remove('bg-black');
    };
  }, []);

  // Sync URL query parameters with filters

  const getDateMemo = (date) => {
    let dateMemo = debouncedFilterValues[date];
    if (dateMemo === undefined) {
      dateMemo = debouncedFilterValues.studyDate[date];
      if (dateMemo)
        return dateMemo;
      const currentDate = new Date();
      return moment(currentDate).format('YYYYMMDD');
    }
    return dateMemo;
  }

  const getStudyes = () => {

    const queryString = {};
    //Período
    try {
      const startDate = getDateMemo('startDate');
      const endDate = getDateMemo('endDate');
      if (startDate)
        queryString['startDate'] = startDate;
      if (endDate)
        queryString['endDate'] = endDate;
    } catch (ex) { }

    //Modalidade
    try {
      if (debouncedFilterValues.modalitiesInStudy === undefined) {
        queryString['modalities'] = debouncedFilterValues.modalities.join(',');
      } else {
        queryString['modalities'] = debouncedFilterValues.modalitiesInStudy.join(',');
      }
    } catch (ex) { }

    const filterFields = [
      'patientName'
      , 'patientId'
      , 'accessionNumber'
      , 'studyDescription'
      , 'mrn'
      , 'accession'
      , 'description'
    ];

    filterFields.forEach(field => {
      const valueFilter = debouncedFilterValues[field];
      if (valueFilter) {
        field = field.replace('patientId', 'mrn');
        field = field.replace('accessionNumber', 'accession');
        field = field.replace('studyDescription', 'description');
        queryString[field] = valueFilter;
      }
    });

    const optionsResult = ['pageNumber', 'resultsPerPage', 'sortBy', 'sortDirection'];
    optionsResult.forEach(obj => {
      const valueObj = debouncedFilterValues[obj];
      if (valueObj) {
        queryString[obj] = valueObj;
      }
    });

    const search = qs.stringify(queryString, {
      skipNull: true,
      skipEmptyString: true,
    });

    navigate({
      pathname: '/',
      search: search ? `?${search}` : undefined,
    });

  }

  useEffect(() => {
    if (!debouncedFilterValues) {
      return;
    }

    getStudyes();

    // const queryString = {};
    // Object.keys(defaultFilterValues).forEach(key => {
    //   const defaultValue = defaultFilterValues[key];
    //   const currValue = debouncedFilterValues[key];

    //   // TODO: nesting/recursion?
    //   if (key === 'studyDate') {
    //     if (
    //       currValue.startDate &&
    //       defaultValue.startDate !== currValue.startDate
    //     ) {
    //       queryString.startDate = currValue.startDate;
    //     }
    //     if (currValue.endDate && defaultValue.endDate !== currValue.endDate) {
    //       queryString.endDate = currValue.endDate;
    //     }
    //   } else if (key === 'modalities' && currValue.length) {
    //     queryString.modalities = currValue.join(',');
    //   } else if (currValue !== defaultValue) {
    //     queryString[key] = currValue;
    //   }
    // });

    // const search = qs.stringify(queryString, {
    //   skipNull: true,
    //   skipEmptyString: true,
    // });

    // navigate({
    //   pathname: '/',
    //   search: search ? `?${search}` : undefined,
    // });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFilterValues]);

  // Query for series information
  useEffect(() => {
    const fetchSeries = async studyInstanceUid => {
      try {
        const series = await dataSource.query.series.search(studyInstanceUid);
        seriesInStudiesMap.set(studyInstanceUid, sortBySeriesDate(series));
        setStudiesWithSeriesData([...studiesWithSeriesData, studyInstanceUid]);
      } catch (ex) {
        // TODO: UI Notification Service
        console.warn(ex);
      }
    };

    // TODO: WHY WOULD YOU USE AN INDEX OF 1?!
    // Note: expanded rows index begins at 1
    for (let z = 0; z < expandedRows.length; z++) {
      const expandedRowIndex = expandedRows[z] - 1;
      const studyInstanceUid = sortedStudies[expandedRowIndex].studyInstanceUid;

      if (studiesWithSeriesData.includes(studyInstanceUid)) {
        continue;
      }

      fetchSeries(studyInstanceUid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedRows, studies]);

  const isFiltering = (filterValues, defaultFilterValues) => {
    return !isEqual(filterValues, defaultFilterValues);
  };

  const rollingPageNumberMod = Math.floor(101 / resultsPerPage);
  const rollingPageNumber = (pageNumber - 1) % rollingPageNumberMod;
  //const offset = resultsPerPage * rollingPageNumber;
  const offset = 0; //* '0' para paginação via banco
  const offsetAndTake = offset + resultsPerPage;

  const totalAllStudies = studies['AllStudies'] ? studies['AllStudies'] : 0;

  const [temLaudo, setTemLaudo] = useState({});
  const [progressBar, setProgressBar] = useState(false);
  const [dialogMax, setDialogMax] = useState(false);
  const [dialogHeader, setDialogHeader] = useState("");
  const [dialogPrintLaudo, setDialogPrintLaudo] = useState(false);

  const destacarLinha = (e) => {
    try {
      const tr = e.target.parentElement.parentElement.parentElement;
      return tr.click();
    } catch (ex) { }
  }

  const setDialogContentSize = () => {
    try {
      setTimeout(() => {
        const pDialogContent = document.getElementsByClassName('p-dialog-content')[0];
        const embed = document.getElementsByTagName('embed')[0];
        pDialogContent.style.padding = '0';
        pDialogContent.style.overflow = 'hidden';
        embed.style.height = `${pDialogContent.clientHeight}px`;
      }, 200);
    } catch (ex) { }
  }

  const adicionarHistorico = (StudyInstanceUid) => {
    try {
      const auth = JSON.parse(sessionStorage.getItem('auth'));
      const usuarioId = auth.Id;
      const operacao = 46;
      const historicoService = new HistoricoService();
      historicoService.postAdicionarHistorico(historicoService.acoesHistorico.baixar, StudyInstanceUid)
      //axios.post(`${window.config.servico}/Api/Pedido/PostAdicionarHistoricoWebViewer?operacao=${operacao}&usuarioId=${usuarioId}&studyUid=${StudyInstanceUid}`)
      //.catch(error => { console.log(error); })
    } catch (error) {
      console.error(error);
    }
  }

  const imprimirLaudo = (e, row) => {
    destacarLinha(e);
    // const value = e.dataGrid;
    setProgressBar(true);
    setDialogMax(false);
    setDialogHeader(`${row.patientName} - ${row.accession}`);
    setDialogPrintLaudo(true);
    setTimeout(() => {
      const caminho = `${window.config.servico}/api/Pedido/GetResultadoPdfPorStudyUid?studyUid=${row.studyInstanceUid}`;
      try {
        document.getElementsByTagName('embed')[0].remove();
      } catch (error) { }

      try {
        const headers = new Headers();
        headers.append('user', `${ServiceApi._getHeaders().headers.user}`);
        headers.append('Cnonce', `${ServiceApi._getHeaders().headers.Cnonce}`);
        headers.append('Token', `${ServiceApi._getHeaders().headers.Token}`);

        const requestOptions = {
          method: 'GET',
          headers: headers,
        };

        fetch(caminho, requestOptions)
          .then(response => {
            return response.blob();
          })
          .then(blob => {
            const embed = document.createElement('embed');
            embed.src = URL.createObjectURL(blob);
            embed.width = '100%';

            document.getElementById(`printLaudo`).append(embed);
            setDialogContentSize();
            adicionarHistorico(row.studyInstanceUid);
          })
          .catch(error => {
            console.error('Erro na requisição:', error);
          });
      } catch (error) {
        console.error('Erro:', error);
      }
      setProgressBar(false);
    }, 500);
  }

  useEffect(() => {
    sortedStudies.forEach(study => {
      PossuiLaudo(study.studyInstanceUid);
    });
  }, [sortedStudies]);

  const laudoService = new LaudoService();

  async function PossuiLaudo(studyUid) {
    try {
      const res = await laudoService.PostPossuiLaudo(studyUid);
      setTemLaudo((prev) => ({
        ...prev,
        [studyUid]: res.data
      }));

    } catch (error) {
      console.log(error);
    }
  }

  const tableDataSource = sortedStudies.map((study, key) => {
    const rowKey = key + 1;
    const isExpanded = expandedRows.some(k => k === rowKey);
    const {
      studyInstanceUid,
      accession,
      modalities,
      instances,
      description,
      mrn,
      patientName,
      date,
      time,
    } = study;
    const studyDate =
      date &&
      moment(date, ['YYYYMMDD', 'YYYY.MM.DD'], true).isValid() &&
      moment(date, ['YYYYMMDD', 'YYYY.MM.DD']).format('MMM-DD-YYYY');
    const studyTime =
      time &&
      moment(time, ['HH', 'HHmm', 'HHmmss', 'HHmmss.SSS']).isValid() &&
      moment(time, ['HH', 'HHmm', 'HHmmss', 'HHmmss.SSS']).format('hh:mm A');

    const valuesSubGrid = (row, fncImprimirLaudo) => {
      const studyInstanceUid = row.StudyInstanceUid;
      const modalities = row.Modalidade;
      return {
        Values: seriesInStudiesMap.has(studyInstanceUid)
          ? seriesInStudiesMap.get(studyInstanceUid).map(s => {
            return {
              description: s.description || '(vazia)',
              seriesNumber: s.seriesNumber ?? '',
              modality: s.modality || '',
              instances: s.numSeriesInstances || '',
            };
          })
          : [],
      }
    }

    const visualizarEstudo = () => {
      const metaDataDisable = window.config.metaDataDisable;
      const url = metaDataDisable
        ? `study?${studyInstanceUid}`
        : `${dataPath ? '../../' : ''}${appConfig.modes[0].routeName}${dataPath || ''}?StudyInstanceUIDs=${studyInstanceUid}`;
      sessionStorage.setItem('lastWorklistUrl', window.location.href);
      window.location.href = url;
    }

    const isModalityPT = async () => {
      const series = await dataSource.query.series.search(studyInstanceUid);
      const modalidades = [];
      for (const serie of series) {
        modalidades.push(serie.modality)
      }
      return modalidades.includes("CT") && modalidades.includes("PT");
    }

    const visualizarTmtv = async () => {
      const isTmtv = await isModalityPT();
      if (isTmtv) {
        sessionStorage.setItem('lastWorklistUrl', window.location.href);
        window.location.href = `tmtv?StudyInstanceUIDs=${studyInstanceUid}`;
      } else {
        uiNotificationService.show({
          title: 'MTV indisponível',
          message: 'A visualização do volume metabólico tumoral deve ser usada nos exames PET/CT.',
          type: 'error',
        });
      }
    }

    function printLaudo(e) {
      imprimirLaudo(e, study);
    }

    return {
      dataGrid: {
        PacienteNome: patientName,
        Prontuario: mrn,
        DataEstudo: studyDate,
        Descricao: description,
        Modalidade: modalities,
        AcNumber: accession,
        StudyInstanceUid: studyInstanceUid,
        Tmtv: <Button disabled={modalities.includes('PT') ? false : true} label='' icon='pi pi-clone' tooltip='Volume Total do Tumor Metabólico' tooltipOptions={{ position: 'bottom' }} style={{ marginLeft: '10px' }} onClick={() => {
          visualizarTmtv();
        }} />,
        View: <Button label='' icon='pi pi-eye' tooltip='Visualizar Exame' tooltipOptions={{ position: 'bottom' }} style={{ marginLeft: '30px' }} onClick={() => {
          visualizarEstudo();
        }} />,
        Laudo: <Button label='' icon='pi pi-print' tooltipOptions={{ position: 'left' }} disabled={!temLaudo[study.studyInstanceUid]} style={{ display: window.config.reportCenter ? '' : 'none', cursor: 'default', marginLeft: '20px' }} onClick={(e) => {
          printLaudo(e);
        }} />,
        Instances: instances,
        ValuesSubGrid: (row, fncImprimirLaudo) => valuesSubGrid(row, fncImprimirLaudo),
        OnClickRow: () =>
          setExpandedRows(s =>
            isExpanded ? s.filter(n => rowKey !== n) : [...s, rowKey]
          ),
        OnDoubleClickRow: () => {
          visualizarEstudo();
        },
        isExpanded
      },

      row: [
        {
          key: 'patientName',
          content: patientName ? (
            <TooltipClipboard>{patientName}</TooltipClipboard>
          ) : (
            <span className="text-gray-700">(Empty)</span>
          ),
          gridCol: 4,
        },
        {
          key: 'mrn',
          content: <TooltipClipboard>{mrn}</TooltipClipboard>,
          gridCol: 3,
        },
        {
          key: 'studyDate',
          content: (
            <>
              {studyDate && <span className="mr-4">{studyDate}</span>}
              {studyTime && <span>{studyTime}</span>}
            </>
          ),
          title: `${studyDate || ''} ${studyTime || ''}`,
          gridCol: 5,
        },
        {
          key: 'description',
          content: <TooltipClipboard>{description}</TooltipClipboard>,
          gridCol: 4,
        },
        {
          key: 'modality',
          content: modalities,
          title: modalities,
          gridCol: 3,
        },
        {
          key: 'accession',
          content: <TooltipClipboard>{accession}</TooltipClipboard>,
          gridCol: 3,
        },
        {
          key: 'instances',
          content: (
            <>
              <Icon
                name="group-layers"
                className={classnames('inline-flex mr-2 w-4', {
                  'text-primary-active': isExpanded,
                  'text-secondary-light': !isExpanded,
                })}
              />
              {instances}
            </>
          ),
          title: (instances || 0).toString(),
          gridCol: 4,
        },
      ],
      // Todo: This is actually running for all rows, even if they are
      // not clicked on.
      expandedContent: (
        <StudyListExpandedRow
          seriesTableColumns={{
            description: 'Description',
            seriesNumber: 'Series',
            modality: 'Modality',
            instances: 'Instances',
          }}
          seriesTableDataSource={
            seriesInStudiesMap.has(studyInstanceUid)
              ? seriesInStudiesMap.get(studyInstanceUid).map(s => {
                return {
                  description: s.description || '(vazia)',
                  seriesNumber: s.seriesNumber ?? '',
                  modality: s.modality || '',
                  instances: s.numSeriesInstances || '',
                };
              })
              : []
          }
        >
          {appConfig.modes.map((mode, i) => {
            const isFirst = i === 0;

            const isValidMode = mode.isValidMode({ modalities });
            // TODO: Modes need a default/target route? We mostly support a single one for now.
            // We should also be using the route path, but currently are not
            // mode.routeName
            // mode.routes[x].path
            // Don't specify default data source, and it should just be picked up... (this may not currently be the case)
            // How do we know which params to pass? Today, it's just StudyInstanceUIDs

            const lastedVersion = window.config.dataSources[0].configuration['lastedVersion'];
            const route = lastedVersion
              ? `${dataPath ? '../../' : ''}study?${studyInstanceUid}`
              : `${dataPath ? '../../' : ''}${mode.routeName}${dataPath || ''}?StudyInstanceUIDs=${studyInstanceUid}`;

            return (
              <Link
                key={i}
                to={route}
              // to={`${mode.routeName}/dicomweb?StudyInstanceUIDs=${studyInstanceUid}`}
              >
                <Button
                  rounded="full"
                  variant={isValidMode ? 'contained' : 'disabled'}
                  disabled={!isValidMode}
                  endIcon={<Icon name="launch-arrow" />} // launch-arrow | launch-info
                  className={classnames('font-medium	', { 'ml-2': !isFirst })}
                  onClick={() => { }}
                >
                  {t(`Modes:${mode.displayName}`)}
                </Button>
              </Link>
            );
          })}
        </StudyListExpandedRow>
      ),
      onClickRow: () =>
        setExpandedRows(s =>
          isExpanded ? s.filter(n => rowKey !== n) : [...s, rowKey]
        ),
      isExpanded,
    };
  });

  const hasStudies = numOfStudies > 0;
  const versionNumber = process.env.VERSION_NUMBER;
  const buildNumber = process.env.BUILD_NUM;

  const menuOptions = [
    {
      title: t('Header:About'),
      icon: 'info',
      onClick: () =>
        show({
          content: AboutModal,
          title: 'About Tzion WebViewer',
          contentProps: { versionNumber, buildNumber },
        }),
    },
    {
      title: t('Header:Preferences'),
      icon: 'settings',
      onClick: () =>
        show({
          title: t('UserPreferencesModal:User Preferences'),
          content: UserPreferences,
          contentProps: {
            hotkeyDefaults: hotkeysManager.getValidHotkeyDefinitions(
              hotkeyDefaults
            ),
            hotkeyDefinitions,
            onCancel: hide,
            currentLanguage: currentLanguage(),
            availableLanguages,
            defaultLanguage,
            onSubmit: state => {
              i18n.changeLanguage(state.language.value);
              hotkeysManager.setHotkeys(state.hotkeyDefinitions);
              hide();
            },
            onReset: () => hotkeysManager.restoreDefaultBindings(),
            hotkeysModule: hotkeys,
          },
        }),
    },
  ];

  if (appConfig.oidc) {
    menuOptions.push({
      icon: 'power-off',
      title: t('Header:Logout'),
      onClick: () => {
        navigate(
          `/logout?redirect_uri=${encodeURIComponent(window.location.href)}`
        );
      },
    });
  }

  const pagination = <StudyListPagination
    onChangePage={onPageNumberChange}
    onChangePerPage={onResultsPerPageChange}
    currentPage={pageNumber}
    perPage={resultsPerPage}
    onChange={setFilterValues}
    filterValues={{ ...filterValues, ...defaultSortValues }}
    totalAllStudies={totalAllStudies}
  />;

  return (
    <div className="bg-black h-screen flex flex-col ">
      <Dialog
        id="dialogPrintLaudo"
        onMaximize={e => {
          setDialogMax(e.maximized);
          setDialogContentSize();
        }}
        header={`Visualizar / Imprimir Laudo - ${dialogHeader} `}
        maximizable
        maximized={dialogMax}
        modal visible={dialogPrintLaudo}
        style={{ width: '80vw', height: '80vh' }}
        contentStyle={{ background: '#ddd', margin: 0, padding: 0, fontSize: '20px' }}
        onHide={() => setDialogPrintLaudo(false)}>
        <div id='printLaudo'>
          <ProgressBar mode='indeterminate' style={{ borderRadius: '0', height: '5px', display: progressBar ? '' : 'none' }} />
        </div>
      </Dialog>

      <Header
        isSticky
        menuOptions={menuOptions}
        isReturnEnabled={false}
        WhiteLabeling={appConfig.whiteLabeling}
      />
      <div className="overflow-y-auto ohif-scrollbar">
        <StudyListFilter
          numOfStudies={pageNumber * resultsPerPage > 100 ? 101 : numOfStudies}
          filtersMeta={filtersMeta}
          filterValues={{ ...filterValues, ...defaultSortValues }}
          onChange={setFilterValues}
          clearFilters={() => setFilterValues(defaultFilterValues)}
          isFiltering={isFiltering(filterValues, defaultFilterValues)}
        />
        {hasStudies ? (
          <>
            <StudyListTable
              tableDataSource={tableDataSource.slice(offset, offsetAndTake)}
              numOfStudies={numOfStudies}
              filtersMeta={filtersMeta}
              filterValues={{ ...filterValues, ...defaultSortValues }}
              onChange={setFilterValues}
            />
            {pagination}
          </>
        ) : (
          <>
            {pagination}
            <div className="flex flex-col items-center justify-center pt-48">
              {appConfig.showLoadingIndicator && isLoadingData ? (
                <LoadingIndicatorProgress className={'w-full h-full bg-black'} />
              ) : (
                <EmptyStudies />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

WorkList.propTypes = {
  data: PropTypes.array.isRequired,
  dataSource: PropTypes.shape({
    query: PropTypes.object.isRequired,
  }).isRequired,
  isLoadingData: PropTypes.bool.isRequired,
};

const defaultFilterValues = {
  patientName: '',
  mrn: '',
  studyDate: {
    startDate: null,
    endDate: null,
  },
  description: '',
  modalities: [],
  accession: '',
  sortBy: '',
  sortDirection: 'none',
  pageNumber: 1,
  resultsPerPage: 25,
  datasources: '',
};

function _tryParseInt(str, defaultValue) {
  let retValue = defaultValue;
  if (str && str.length > 0) {
    if (!isNaN(str)) {
      retValue = parseInt(str);
    }
  }
  return retValue;
}

function _getQueryFilterValues(params) {
  const queryFilterValues = {
    patientName: params.get('patientname'),
    mrn: params.get('mrn'),
    studyDate: {
      startDate: params.get('startdate'),
      endDate: params.get('enddate'),
    },
    description: params.get('description'),
    modalities: params.get('modalities')
      ? params.get('modalities').split(',')
      : [],
    accession: params.get('accession'),
    sortBy: params.get('sortby'),
    sortDirection: params.get('sortdirection'),
    pageNumber: _tryParseInt(params.get('pagenumber'), undefined),
    resultsPerPage: _tryParseInt(params.get('resultsperpage'), undefined),
    datasources: params.get('datasources'),
  };

  // Delete null/undefined keys
  Object.keys(queryFilterValues).forEach(
    key => queryFilterValues[key] == null && delete queryFilterValues[key]
  );

  return queryFilterValues;
}

function _sortStringDates(s1, s2, sortModifier) {
  // TODO: Delimiters are non-standard. Should we support them?
  const s1Date = moment(s1.date, ['YYYYMMDD', 'YYYY.MM.DD'], true);
  const s2Date = moment(s2.date, ['YYYYMMDD', 'YYYY.MM.DD'], true);

  if (s1Date.isValid() && s2Date.isValid()) {
    return (
      (s1Date.toISOString() > s2Date.toISOString() ? 1 : -1) * sortModifier
    );
  } else if (s1Date.isValid()) {
    return sortModifier;
  } else if (s2Date.isValid()) {
    return -1 * sortModifier;
  }
}

export default WorkList;
